import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LibraryBooks as LibraryBooksIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getCourses,
  createCourse,
  updateCourse,
  deleteCourse,
} from '../services/api.js';

const AdminCourseManagement = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);

  // Form state
  // Assuming minimal course fields for creation/edit. Adjust as per actual backend schema.
  // We'll use nome, descricao, preco, ch_total, active based on common course models.
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    preco: 0,
    ch_total: 0,
    active: true,
  });

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCourses(1, 100); // Fetching up to 100 courses for admin view initially
      setCourses(
        data.cursos || data.items || (Array.isArray(data) ? data : [])
      ); // Extract the array safely
      setError(null);
    } catch (err) {
      setError('Erro ao carregar cursos: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleOpenDialog = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        nome: course.nome || '',
        descricao: course.descricao || '',
        preco: course.preco || 0,
        ch_total: course.ch_total || 0,
        active: course.active !== undefined ? course.active : true,
      });
    } else {
      setEditingCourse(null);
      setFormData({
        nome: '',
        descricao: '',
        preco: 0,
        ch_total: 0,
        active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCourse(null);
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const submitData = new window.FormData();
      submitData.append('nome', formData.nome);
      submitData.append('descricao', formData.descricao);
      submitData.append('preco', formData.preco);
      submitData.append('ch_total', formData.ch_total);
      submitData.append('active', formData.active);

      if (editingCourse) {
        // Use standard JSON for update if not updating image, or FormData depending on API
        // Wait, updateCourse in api.js just uses 'data'. We use JSON for patch.
        await updateCourse(editingCourse.id_curso, {
          nome: formData.nome,
          descricao: formData.descricao,
          preco: formData.preco,
          ch_total: formData.ch_total,
          active: formData.active,
        });
        setSuccess('Curso atualizado com sucesso!');
      } else {
        await createCourse(submitData); // Requires FormData based on api.js implementation
        setSuccess('Curso criado com sucesso!');
      }
      handleCloseDialog();
      fetchCourses();
    } catch (err) {
      setError(err.message || 'Erro ao salvar curso');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async courseId => {
    if (
      window.confirm(
        'Tem certeza que deseja excluir este curso? Esta ação não pode ser desfeita.'
      )
    ) {
      try {
        setLoading(true);
        await deleteCourse(courseId);
        setSuccess('Curso excluído com sucesso!');
        fetchCourses();
      } catch (err) {
        setError(err.message || 'Erro ao excluir curso');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleManageContent = courseId => {
    navigate(`/admin/curso/${courseId}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant='h5' component='h2'>
          Gerenciamento de Cursos
        </Typography>
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Curso
        </Button>
      </Box>

      {loading && !courses.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Carga Horária</TableCell>
                <TableCell>Preço</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right'>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    Nenhum curso encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                courses.map(course => (
                  <TableRow key={course.id_curso}>
                    <TableCell>{course.id_curso}</TableCell>
                    <TableCell>{course.nm_titulo}</TableCell>
                    <TableCell>{course.ch_total}h</TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(course.preco)}
                    </TableCell>
                    <TableCell>{course.active ? 'Ativo' : 'Inativo'}</TableCell>
                    <TableCell align='right'>
                      <Tooltip title='Gerenciar Conteúdo'>
                        <IconButton
                          color='primary'
                          onClick={() => handleManageContent(course.id_curso)}
                        >
                          <LibraryBooksIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Editar'>
                        <IconButton
                          color='info'
                          onClick={() => handleOpenDialog(course)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Excluir'>
                        <IconButton
                          color='error'
                          onClick={() => handleDelete(course.id_curso)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog para Criar/Editar Curso */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editingCourse ? 'Editar Curso' : 'Novo Curso'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label='Nome do Curso'
              name='nome'
              value={formData.nome}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label='Descrição'
              name='descricao'
              value={formData.descricao}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
            />
            <TextField
              label='Carga Horária (horas)'
              name='ch_total'
              type='number'
              value={formData.ch_total}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label='Preço (R$)'
              name='preco'
              type='number'
              value={formData.preco}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant='contained'
            color='primary'
            disabled={loading || !formData.nome}
          >
            {loading ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert
          onClose={() => setSuccess(null)}
          severity='success'
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert
          onClose={() => setError(null)}
          severity='error'
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminCourseManagement;
